/** Step keyboard shortcuts — maps key presses to option selection, submit, navigation, and overlays. */
import { useEffect, useCallback } from 'react';
import { useSession } from '../SessionContext';
import { useKeyboardRipple } from '../components/KeyboardRipple';
import type {
  McqConfig,
  RankingConfig,
  ClozeConfig,
  ClozeDiagramConfig,
  ClozeImageConfig,
  ClassifyConfig,
} from '../types';

// ─── Public interface ──────────────────────────────────────────────────────

interface UseStepKeyboardOptions {
  onSubmit?: () => void;
  /** Called when advancing past an info step (sends server-side submit). */
  onSubmitInfo?: () => void;
  onExit?: () => void;
  onToggleShortcuts?: () => void;
  /** When true, all keyboard handling is suppressed (e.g. during confidence check overlay). */
  disabled?: boolean;
  /** When true, forward navigation keys (Enter/Space/ArrowRight) are blocked but other shortcuts still work. */
  blockNext?: boolean;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function isTextInputFocused(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useStepKeyboard({
  onSubmit,
  onSubmitInfo,
  onExit,
  onToggleShortcuts,
  disabled,
  blockNext,
}: UseStepKeyboardOptions): void {
  const { state, dispatch, canSubmit, canGoNext, canGoBack, currentStep } = useSession();
  const { triggerRipple } = useKeyboardRipple();

  const { phase, answer, isReviewing } = state;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (disabled) return;
      // No-op when there is no current step
      if (!currentStep) return;

      const isTextInput = isTextInputFocused(e.target);

      // ── ? toggle shortcut overlay (works everywhere, ignore repeat) ──
      if (e.key === '?' && !isTextInput && !e.repeat) {
        e.preventDefault();
        onToggleShortcuts?.();
        return;
      }

      // ── Enter / Space ──────────────────────────────────────────────────
      // Space acts like Enter but only when a text input is NOT focused
      // (so users can still type spaces in free-text fields).
      if (e.key === 'Enter' || (e.key === ' ' && !isTextInput)) {
        if (e.key === 'Enter' && isTextInput && !canSubmit && !canGoNext) return;
        e.preventDefault();
        if (canSubmit && onSubmit) {
          onSubmit();
        } else if (canGoNext && !blockNext) {
          if (currentStep?.stepKind === 'info' && onSubmitInfo && !isReviewing) {
            onSubmitInfo();
          } else {
            dispatch({ type: 'NEXT_STEP' });
          }
        }
        return;
      }

      // ── Arrow navigation (not in text input) ──────────────────────────
      if (!isTextInput) {
        if (e.key === 'ArrowLeft' && canGoBack) {
          e.preventDefault();
          dispatch({ type: 'GO_BACK' });
          return;
        }
        if (e.key === 'ArrowRight' && canGoNext && !blockNext) {
          e.preventDefault();
          if (currentStep?.stepKind === 'info' && onSubmitInfo && !isReviewing) {
            onSubmitInfo();
          } else {
            dispatch({ type: 'NEXT_STEP' });
          }
          return;
        }
      }

      // ── Escape ─────────────────────────────────────────────────────────
      if (e.key === 'Escape') {
        e.preventDefault();
        onExit?.();
        return;
      }

      // ── Step-type shortcuts (not in text input, only during answerable phases)
      const isAnswerablePhase = phase === 'idle' || phase === 'answering';
      if (!isTextInput && isAnswerablePhase) {
        const config = currentStep.stepSnapshotPublicJson.config;

        // ── True/False: T/F keys (hidden shortcuts, not shown in overlay) ─
        if (currentStep.questionType === 'true_false') {
          const lower = e.key.toLowerCase();
          if (lower === 't' || lower === 'f') {
            e.preventDefault();
            const value = lower === 't';
            dispatch({
              type: 'UPDATE_ANSWER',
              answer: { type: 'true_false', selected: value },
            });
            triggerRipple(lower === 't' ? 0 : 1);
            return;
          }
        }

        const numKey = parseInt(e.key, 10);

        if (!isNaN(numKey) && numKey >= 1 && numKey <= 9) {
          const index = numKey - 1;

          // ── MCQ number keys ──────────────────────────────────────────
          if (currentStep.questionType === 'mcq') {
            const mcqConfig = config as unknown as McqConfig;
            if (!mcqConfig.options || index >= mcqConfig.options.length) return;

            const targetId = mcqConfig.options[index].id;
            const currentAnswer = answer as { type: 'mcq'; selectedIds: string[] } | null;
            const selectedIds = currentAnswer?.type === 'mcq' ? [...currentAnswer.selectedIds] : [];

            if (mcqConfig.allowMultiple) {
              const existing = selectedIds.indexOf(targetId);
              if (existing >= 0) {
                selectedIds.splice(existing, 1);
              } else {
                selectedIds.push(targetId);
              }
            } else {
              selectedIds.length = 0;
              selectedIds.push(targetId);
            }

            dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'mcq', selectedIds } });
            triggerRipple(index);
          }

          // ── True/False number keys (1=True, 2=False) ───────────────
          if (currentStep.questionType === 'true_false') {
            if (index > 1) return;
            const value = index === 0; // 1=true, 2=false
            dispatch({
              type: 'UPDATE_ANSWER',
              answer: { type: 'true_false', selected: value },
            });
            triggerRipple(index);
          }

          // ── Ranking number keys ──────────────────────────────────────
          if (currentStep.questionType === 'ranking') {
            const rankingConfig = config as unknown as RankingConfig;
            if (!rankingConfig.options || index >= rankingConfig.options.length) return;

            const targetId = rankingConfig.options[index].id;
            const currentAnswer = answer as { type: 'ranking'; order: string[] } | null;
            const order = currentAnswer?.type === 'ranking' ? [...currentAnswer.order] : [];

            if (order.includes(targetId)) return;

            order.push(targetId);
            dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'ranking', order } });
            triggerRipple(index);
          }

          // ── Cloze MC number keys (pick from pool) ──────────────────
          if (currentStep.questionType === 'cloze') {
            const clozeConfig = config as unknown as ClozeConfig;
            if (clozeConfig.mode !== 'multiple_choice') return;

            // Pool is pre-shuffled by server
            const allOptions = clozeConfig.options ?? [];
            if (index >= allOptions.length) return;

            const optionText = allOptions[index];
            const currentAnswer = answer as {
              type: 'cloze';
              blanks: Record<string, string>;
            } | null;
            const blanks = currentAnswer?.type === 'cloze' ? { ...currentAnswer.blanks } : {};

            // Skip if this option is already used in a blank
            if (Object.values(blanks).includes(optionText)) return;

            // Fill the next empty blank
            for (const blank of clozeConfig.blanks) {
              if (!blanks[blank.id]) {
                blanks[blank.id] = optionText;
                dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'cloze', blanks } });
                triggerRipple(index);
                return;
              }
            }
          }

          // ── Cloze Diagram MC number keys (pick from pool) ──────────
          if (currentStep.questionType === 'cloze_diagram') {
            const cdConfig = config as unknown as ClozeDiagramConfig;
            if (cdConfig.mode !== 'multiple_choice') return;

            const allOptions = cdConfig.options ?? [];
            if (index >= allOptions.length) return;

            const optionText = allOptions[index];
            const currentAnswer = answer as {
              type: 'cloze_diagram';
              blanks: Record<string, string>;
            } | null;
            const blanks =
              currentAnswer?.type === 'cloze_diagram' ? { ...currentAnswer.blanks } : {};

            // Skip if this option is already used in a blank
            if (Object.values(blanks).includes(optionText)) return;

            // Fill the next empty blank (position order)
            const blanksInOrder = [...cdConfig.blanks].sort((a, b) => a.position - b.position);
            for (const blank of blanksInOrder) {
              if (!blanks[blank.id]) {
                blanks[blank.id] = optionText;
                dispatch({
                  type: 'UPDATE_ANSWER',
                  answer: { type: 'cloze_diagram', blanks },
                });
                triggerRipple(index);
                return;
              }
            }
          }

          // ── Cloze Image MC number keys (pick from pool) ──────────
          if (currentStep.questionType === 'cloze_image') {
            const ciConfig = config as unknown as ClozeImageConfig;
            if (ciConfig.mode !== 'multiple_choice') return;

            const allOptions = ciConfig.options ?? [];
            if (index >= allOptions.length) return;

            const optionText = allOptions[index];
            const currentAnswer = answer as {
              type: 'cloze_image';
              blanks: Record<string, string>;
            } | null;
            const blanks = currentAnswer?.type === 'cloze_image' ? { ...currentAnswer.blanks } : {};

            // Skip if this option is already used in a blank
            if (Object.values(blanks).includes(optionText)) return;

            // Fill the next empty blank
            for (const blank of ciConfig.blanks) {
              if (!blanks[blank.id]) {
                blanks[blank.id] = optionText;
                dispatch({
                  type: 'UPDATE_ANSWER',
                  answer: { type: 'cloze_image', blanks },
                });
                triggerRipple(index);
                return;
              }
            }
          }

          // ── Classify number keys (assign to category L→R) ──────────
          if (currentStep.questionType === 'classify') {
            const classifyConfig = config as unknown as ClassifyConfig;
            if (!classifyConfig.categories || index >= classifyConfig.categories.length) return;

            const targetCategoryId = classifyConfig.categories[index].id;
            const currentAnswer = answer as {
              type: 'classify';
              assignments: Record<string, string>;
            } | null;
            const assignments =
              currentAnswer?.type === 'classify' ? { ...currentAnswer.assignments } : {};

            // Find the first unassigned item (left to right)
            for (const item of classifyConfig.items) {
              if (!assignments[item.id]) {
                assignments[item.id] = targetCategoryId;
                dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'classify', assignments } });
                return;
              }
            }
          }

          return;
        }

        // ── Backspace: undo last (ranking + cloze MC) ──────────────────
        if (e.key === 'Backspace') {
          e.preventDefault();

          if (currentStep.questionType === 'ranking') {
            const currentAnswer = answer as { type: 'ranking'; order: string[] } | null;
            const order = currentAnswer?.type === 'ranking' ? [...currentAnswer.order] : [];
            if (order.length === 0) return;
            order.pop();
            dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'ranking', order } });
          }

          if (currentStep.questionType === 'cloze') {
            const clozeConfig = config as unknown as ClozeConfig;
            if (clozeConfig.mode !== 'multiple_choice') return;

            const currentAnswer = answer as {
              type: 'cloze';
              blanks: Record<string, string>;
            } | null;
            const blanks = currentAnswer?.type === 'cloze' ? { ...currentAnswer.blanks } : {};

            // Clear the last filled blank
            for (let i = clozeConfig.blanks.length - 1; i >= 0; i--) {
              const blankId = clozeConfig.blanks[i].id;
              if (blanks[blankId]) {
                blanks[blankId] = '';
                dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'cloze', blanks } });
                return;
              }
            }
          }

          if (currentStep.questionType === 'cloze_diagram') {
            const cdConfig = config as unknown as ClozeDiagramConfig;
            if (cdConfig.mode !== 'multiple_choice') return;

            const currentAnswer = answer as {
              type: 'cloze_diagram';
              blanks: Record<string, string>;
            } | null;
            const blanks =
              currentAnswer?.type === 'cloze_diagram' ? { ...currentAnswer.blanks } : {};

            // Clear the last filled blank (position order, backwards)
            const blanksInOrder = [...cdConfig.blanks].sort((a, b) => a.position - b.position);
            for (let i = blanksInOrder.length - 1; i >= 0; i--) {
              const blankId = blanksInOrder[i].id;
              if (blanks[blankId]) {
                blanks[blankId] = '';
                dispatch({
                  type: 'UPDATE_ANSWER',
                  answer: { type: 'cloze_diagram', blanks },
                });
                return;
              }
            }
          }

          if (currentStep.questionType === 'cloze_image') {
            const ciConfig = config as unknown as ClozeImageConfig;
            if (ciConfig.mode !== 'multiple_choice') return;

            const currentAnswer = answer as {
              type: 'cloze_image';
              blanks: Record<string, string>;
            } | null;
            const blanks = currentAnswer?.type === 'cloze_image' ? { ...currentAnswer.blanks } : {};

            // Clear the last filled blank
            for (let i = ciConfig.blanks.length - 1; i >= 0; i--) {
              const blankId = ciConfig.blanks[i].id;
              if (blanks[blankId]) {
                blanks[blankId] = '';
                dispatch({
                  type: 'UPDATE_ANSWER',
                  answer: { type: 'cloze_image', blanks },
                });
                return;
              }
            }
          }

          if (currentStep.questionType === 'classify') {
            const classifyConfig = config as unknown as ClassifyConfig;
            const currentAnswer = answer as {
              type: 'classify';
              assignments: Record<string, string>;
            } | null;
            const assignments =
              currentAnswer?.type === 'classify' ? { ...currentAnswer.assignments } : {};

            // Remove the last assigned item
            for (let i = classifyConfig.items.length - 1; i >= 0; i--) {
              const itemId = classifyConfig.items[i].id;
              if (assignments[itemId]) {
                delete assignments[itemId];
                dispatch({
                  type: 'UPDATE_ANSWER',
                  answer: { type: 'classify', assignments: { ...assignments } },
                });
                return;
              }
            }
          }
        }
      }
    },
    [
      canSubmit,
      canGoNext,
      canGoBack,
      phase,
      answer,
      currentStep,
      dispatch,
      onSubmit,
      onSubmitInfo,
      isReviewing,
      onExit,
      onToggleShortcuts,
      triggerRipple,
      disabled,
      blockNext,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}
